import { useCallback, useEffect, useRef, useState } from "react";
import {
  ActiveJobArgs,
  JobToastType,
  selectActiveJobArgsByType,
} from "@shared/slices/jobs.slice";
import { useAppSelector } from "app/hooks";
import { useMemo } from "react";

/**
 * Keeps a local copy of active job args for a given job type.
 * New jobs are added when they appear in Redux.
 * Jobs are only removed locally when `removeJob(id)` is called,
 * which should happen when the user dismisses the toast AND the job is no longer active in Redux.
 */
export function useLocalJobData(type: JobToastType): {
  localJobs: ActiveJobArgs[];
  removeJob: (id: number) => void;
  isJobActiveInRedux: (id: number) => boolean;
} {
  const selector = useMemo(() => selectActiveJobArgsByType(type), [type]);
  const reduxJobs = useAppSelector(selector);
  const [localJobs, setLocalJobs] = useState<ActiveJobArgs[]>([]);
  const reduxJobsRef = useRef(reduxJobs);
  reduxJobsRef.current = reduxJobs;

  // When new jobs appear in Redux, add them locally
  useEffect(() => {
    setLocalJobs((prev) => {
      const existingIds = new Set(prev.map((j) => j.id));
      const newJobs = reduxJobs.filter((j) => !existingIds.has(j.id));
      if (newJobs.length === 0) return prev;
      return [...prev, ...newJobs];
    });
  }, [reduxJobs]);

  const removeJob = useCallback((id: number) => {
    setLocalJobs((prev) => prev.filter((j) => j.id !== id));
  }, []);

  const isJobActiveInRedux = useCallback(
    (id: number) =>
      reduxJobsRef.current.some((j) => j.id === id && j.is_active),
    []
  );

  return { localJobs, removeJob, isJobActiveInRedux };
}
